import type {
	DriverNowStatus,
	TransportAllocationOptions,
	TransportAttentionKind,
	TransportCheckIssue,
	TransportCheckResult,
	TransportDisplayStatus,
	TransportDriverOption,
	TransportRowCheckCode,
	TransportNocSummary,
	TransportSuggestion,
	TransportSuggestionDayCount,
	TransportSuggestionsResult,
	TransportSummary,
	TransportSummaryResult,
	TransportTotalCheckCode,
	TransportVehicleOption,
	VehicleType,
} from "@seagames/contracts";
import { wallTimeIn } from "../format/datetime";

/**
 * Presentation helpers for the dispatch screens. Pure functions; no rule
 * lives here that the API does not already enforce — the allocation
 * pre-check only spares a round trip, `transport_check_allocations` is the
 * judge (422 ALLOCATION_INVALID replaces whatever this said).
 */

/** Rows per page offered on the Requests list (each ≤ PAGE_SIZE_MAX; the default is the contract's TRANSPORT_REQUESTS_PAGE_SIZE_DEFAULT). */
export const TRANSPORT_REQUEST_PAGE_SIZES = [25, 50, 100] as const;

// ── Summary (`GET /transport/summary`) ───────────────────────────────────────
// Counts arrive as lists — every enum member, in the contract's order, zeros
// kept — never as objects keyed by the enum (the API's key casing would
// rewrite them). Read them by value.

/** The full summary (admins, dispatch desks, providers); undefined for a NOC's or while loading. */
export const deskSummary = (s: TransportSummaryResult | undefined): TransportSummary | undefined => (s && !("view" in s) ? s : undefined);
/** A NOC's own summary (`view: "noc"`); undefined otherwise. */
export const nocSummary = (s: TransportSummaryResult | undefined): TransportNocSummary | undefined => (s && "view" in s ? s : undefined);

const countBy = <K extends string, T extends Record<K, string> & { count: number }>(list: readonly T[], key: K, value: string) =>
	list.find((x) => x[key] === value)?.count ?? 0;

/** Trips of the day with this derived status. */
export const statusCount = (s: Pick<TransportSummary, "by_status"> | Pick<TransportNocSummary, "by_status">, status: TransportDisplayStatus): number =>
	countBy(s.by_status as readonly { status: string; count: number }[], "status", status);
/** A NOC's trips on the Upcoming tab with this status — the list's total for `status=`. */
export const upcomingCount = (s: Pick<TransportNocSummary, "upcoming_by_status">, status: Exclude<TransportDisplayStatus, "SHORT">): number =>
	countBy(s.upcoming_by_status as readonly { status: string; count: number }[], "status", status);
/** One Needs-attention kind's count. */
export const attentionCount = (s: Pick<TransportSummary, "attention">, kind: TransportAttentionKind): number => countBy(s.attention, "kind", kind);
/** Drivers by their status now. */
export const driverCount = (s: Pick<TransportSummary, "drivers">, status: DriverNowStatus): number => countBy(s.drivers.by_status, "status", status);
/** Vehicles (not retired) of a type. */
export const vehicleTypeCount = (s: Pick<TransportSummary, "vehicles">, type: VehicleType): number => countBy(s.vehicles.by_type, "vehicle_type", type);

/** The day of an instant in the games' zone, `YYYY-MM-DD`. */
export const dayIn = (iso: string | null | undefined, timeZone: string): string | null => wallTimeIn(iso, timeZone)?.date ?? null;

/** Whole minutes from `a` to `b` (positive when `b` is later), null when either is missing. */
export function minutesBetween(a: string | null | undefined, b: string | null | undefined): number | null {
	if (!a || !b) return null;
	const d = Date.parse(b) - Date.parse(a);
	return Number.isNaN(d) ? null : Math.round(d / 60_000);
}

/** 130 → { h: 2, m: 10 }. Sign dropped; the caller says "later" / "earlier". */
export function splitMinutes(total: number): { h: number; m: number } {
	const abs = Math.abs(Math.round(total));
	return { h: Math.floor(abs / 60), m: abs % 60 };
}

// ── Status ──────────────────────────────────────────────────────────────────

/** The four steps of her detail header; which are done for a derived status. */
export const TRIP_STEPS = ["requested", "assigned", "enRoute", "completed"] as const;
export type TripStep = (typeof TRIP_STEPS)[number];

export function stepsDone(status: TransportDisplayStatus): Record<TripStep, boolean> {
	const assigned = status === "ASSIGNED" || status === "SHORT" || status === "EN_ROUTE" || status === "PARTIAL" || status === "COMPLETED";
	const road = status === "EN_ROUTE" || status === "PARTIAL" || status === "COMPLETED";
	return { requested: true, assigned, enRoute: road, completed: status === "COMPLETED" };
}

/** Statuses a bulk Cancel may take (her `cancelReqSheet`): nothing on the road, not closed. */
export const cancellable = (s: TransportDisplayStatus): boolean => s === "PENDING" || s === "SHORT" || s === "ASSIGNED";
/** Reject is only for trips without a driver ("cancel it instead"). */
export const rejectable = (s: TransportDisplayStatus): boolean => s === "PENDING";
/** Auto-assign takes only trips with no vehicle yet (ALREADY_ASSIGNED otherwise). */
export const waitingForDriver = (s: TransportDisplayStatus): boolean => s === "PENDING";

// ── Suggestions ─────────────────────────────────────────────────────────────

/** Where a suggestion ends up, as one key: same entry point + same destination (+ direction) can share a vehicle. */
export const mergeKey = (s: Pick<TransportSuggestion, "direction" | "entry_point" | "place">): string | null =>
	s.place ? `${s.direction}|${s.entry_point.entry_point_id}|${s.place.kind}:${s.place.id}` : null;

/**
 * Q3 "can merge": other suggestions with the same entry point and
 * place whose expected time is within `spreadMinutes` (the games'
 * `group_spread_minutes`, the cascade's own "too far from its group" — the
 * contract leaves this call to the client). Offered, never done for the
 * desk. For each row, the keys of the rows it can merge with.
 */
export function mergeCandidates(rows: readonly TransportSuggestion[], spreadMinutes: number): Map<string, string[]> {
	const out = new Map<string, string[]>();
	const byKey = new Map<string, TransportSuggestion[]>();
	for (const r of rows) {
		const k = mergeKey(r);
		if (!k) continue;
		byKey.set(k, [...(byKey.get(k) ?? []), r]);
	}
	for (const group of byKey.values()) {
		if (group.length < 2) continue;
		for (const r of group) {
			const near = group.filter((o) => o.suggestion_key !== r.suggestion_key && Math.abs(minutesBetween(r.expected_at, o.expected_at) ?? Number.POSITIVE_INFINITY) <= spreadMinutes);
			if (near.length > 0) out.set(r.suggestion_key, near.map((o) => o.suggestion_key));
		}
	}
	return out;
}

/** Selected suggestions can become ONE transfer: same direction, entry point and place. */
export function canMerge(rows: readonly TransportSuggestion[]): boolean {
	if (rows.length < 2) return false;
	const k = mergeKey(rows[0]!);
	return k !== null && rows.every((r) => mergeKey(r) === k);
}

// ── The travel chain: day badges, "Next", deep-link focus ───────────────────

/** A flight's A&D journey key (`group_key` of `/travel/journeys` for a flight leg: `f:<flight_id>`). */
export const flightJourneyKey = (flightId: string): string => `f:${flightId}`;

/** People waiting for a transfer on a day in one direction (= that day's suggestions' Σ people: the badge). */
export const waitingPeople = (d: TransportSuggestionDayCount | undefined, direction: "ARRIVAL" | "DEPARTURE"): number =>
	d ? (direction === "ARRIVAL" ? d.arrivals.people : d.departures.people) : 0;

/**
 * "Next: Mon 27 Oct · 23 waiting →": the first day AFTER `date` with people
 * waiting in this direction, from the day-counts the chips already fetched
 * (ascending, only days with anyone waiting). Null when none is in reach.
 */
export function nextWaitingDay(days: readonly TransportSuggestionDayCount[] | undefined, date: string, direction: "ARRIVAL" | "DEPARTURE"): { date: string; people: number } | null {
	for (const d of days ?? []) {
		if (d.date <= date) continue;
		const people = waitingPeople(d, direction);
		if (people > 0) return { date: d.date, people };
	}
	return null;
}

/**
 * Where a deep link lands on the suggestions (`leg=` from a record, `journey=`
 * from A&D): the suggestion holding the leg (its row is highlighted, picked and
 * offered as "the whole group" / "only this person"), or the waiting row of
 * that leg, else every suggestion and waiting row of the journey. `missing`:
 * a leg was asked for and is on neither list (on a transfer already, no
 * transport needed, or another day / direction).
 */
export interface FocusTargets {
	/** The suggestion holding the leg. */
	legRow: TransportSuggestion | null;
	/** The waiting row of the leg. */
	legWaiting: TransportSuggestionsResult["waiting"]["items"][number] | null;
	suggestionKeys: string[];
	waitingLegIds: string[];
	missing: boolean;
}

export function focusTargets(data: Pick<TransportSuggestionsResult, "items" | "waiting"> | undefined, focus: { leg?: string | undefined; journey?: string | undefined }): FocusTargets {
	const none: FocusTargets = { legRow: null, legWaiting: null, suggestionKeys: [], waitingLegIds: [], missing: false };
	if (!data || (!focus.leg && !focus.journey)) return none;
	if (focus.leg) {
		const leg = focus.leg.toLowerCase();
		const legRow = data.items.find((r) => r.leg_ids.includes(leg)) ?? null;
		const legWaiting = legRow ? null : (data.waiting.items.find((w) => w.travel_leg_id === leg) ?? null);
		return {
			legRow,
			legWaiting,
			suggestionKeys: legRow ? [legRow.suggestion_key] : [],
			waitingLegIds: legWaiting ? [legWaiting.travel_leg_id] : [],
			missing: !legRow && !legWaiting,
		};
	}
	return {
		...none,
		suggestionKeys: data.items.filter((r) => r.journey_key === focus.journey).map((r) => r.suggestion_key),
		waitingLegIds: data.waiting.items.filter((w) => w.journey_key === focus.journey).map((w) => w.travel_leg_id),
	};
}

/**
 * "Only this person": the suggestion cut to ONE leg, for the create dialog
 * (`POST /transport/transfers` with that leg alone — the API derives the
 * pickup from it and keeps following its flight). Its own key, so it never
 * collides with the whole group's.
 */
export function singleLegRow(row: TransportSuggestion, legId: string): TransportSuggestion {
	return { ...row, suggestion_key: `${row.suggestion_key}#${legId}`, leg_ids: [legId], people: 1, wheelchair: 0, organizations: [], organizations_more: 0 };
}

// ── Allocation pre-check (mirrors `transport_check_allocations`) ────────────

export interface EditorRow {
	/** Stable React key. */
	key: string;
	transport_allocation_id?: string;
	vehicle_id: string | null;
	driver_id: string | null;
	/** As typed. */
	pax: string;
}

export const paxOf = (row: EditorRow): number | null => (/^\s*\d+\s*$/.test(row.pax) ? Number(row.pax) : null);

/**
 * The codes her `check()` would give, from what `/options` already says
 * (busy, certified, shift, rest day, cluster). Same codes and order as the
 * SQL function, so the server's answer reads the same when it differs.
 */
export function precheck(rows: readonly EditorRow[], options: TransportAllocationOptions | undefined, seatsNeeded: number, wheelchair: number): TransportCheckResult {
	const vehicles = new Map((options?.vehicles ?? []).map((v) => [v.vehicle_id, v]));
	const drivers = new Map((options?.drivers ?? []).map((d) => [d.driver_id, d]));
	const seenV = new Set<string>();
	const seenD = new Set<string>();
	let seated = 0;
	let rampSeats = 0;
	let otherCluster = false;
	const out: TransportCheckResult["rows"] = [];
	rows.forEach((row, index) => {
		const issues: TransportCheckIssue<TransportRowCheckCode>[] = [];
		const add = (code: TransportRowCheckCode, ref: string | null = null) => issues.push({ code, ref, n: null });
		const v: TransportVehicleOption | undefined = row.vehicle_id ? vehicles.get(row.vehicle_id) : undefined;
		const d: TransportDriverOption | undefined = row.driver_id ? drivers.get(row.driver_id) : undefined;
		const pax = paxOf(row);
		if (!row.vehicle_id) add("VEHICLE_REQUIRED");
		if (!row.driver_id) add("DRIVER_REQUIRED");
		if (pax === null || pax < 1) add("PAX_INVALID");
		if (v && pax !== null && pax > v.seats) add("OVER_CAPACITY");
		if (v && v.status !== "ACTIVE") add("VEHICLE_INACTIVE");
		if (row.vehicle_id && seenV.has(row.vehicle_id)) add("VEHICLE_DUPLICATE");
		if (v?.busy_ref) add("VEHICLE_BUSY", v.busy_ref);
		if (v && d && !d.vehicle_types.includes(v.vehicle_type)) add("DRIVER_NOT_CERTIFIED");
		if (d?.rest_day) add("DRIVER_REST_DAY");
		else if (d && !d.on_shift) add("DRIVER_OFF_SHIFT");
		if (row.driver_id && seenD.has(row.driver_id)) add("DRIVER_DUPLICATE");
		if (d?.busy_ref) add("DRIVER_BUSY", d.busy_ref);
		if (row.vehicle_id) seenV.add(row.vehicle_id);
		if (row.driver_id) seenD.add(row.driver_id);
		if (pax !== null && pax > 0) {
			seated += pax;
			if (v?.has_ramp) rampSeats += pax;
		}
		if ((v && !v.in_dispatch_cluster) || (d && !d.in_dispatch_cluster)) otherCluster = true;
		if (issues.length) out.push({ index, issues });
	});
	const totals: TransportCheckIssue<TransportTotalCheckCode>[] = [];
	const total = (code: TransportTotalCheckCode, n: number | null = null) => totals.push({ code, ref: null, n });
	// Her check() lists both with no vehicle: "Add at least one vehicle" and "N more passengers to seat".
	if (rows.length === 0) total("NO_ROWS");
	if (seated < seatsNeeded) total("SEATS_SHORT", seatsNeeded - seated);
	else if (seated > seatsNeeded) total("SEATS_OVER", seated - seatsNeeded);
	if (wheelchair > 0 && rows.length > 0 && rampSeats < wheelchair) total("RAMP_REQUIRED", wheelchair - rampSeats);
	if (otherCluster) total("OTHER_CLUSTER");
	const result: TransportCheckResult = { rows: out, totals, seats_needed: seatsNeeded, seats_assigned: seated, ok: false };
	result.ok = checkPasses(result, false);
	return result;
}

/** Seats filled by the rows, counting only whole numbers. */
export const seatedOf = (rows: readonly EditorRow[]): number => rows.reduce((n, r) => n + Math.max(0, paxOf(r) ?? 0), 0);

/**
 * Whether the check lets "Confirm" go: no row code, and no total code other
 * than the warning (OTHER_CLUSTER) and — when the desk accepts a partial
 * fill — SEATS_SHORT.
 */
export function checkPasses(check: Pick<TransportCheckResult, "rows" | "totals">, acceptPartial: boolean): boolean {
	if (check.rows.length > 0) return false;
	return check.totals.every((t) => t.code === "OTHER_CLUSTER" || (acceptPartial && t.code === "SEATS_SHORT"));
}

/** A driver who suits a vehicle for this trip: certified for its type, free, on shift, not resting, not used on another row. */
export function driverFits(d: TransportDriverOption, v: TransportVehicleOption, usedDriverIds: ReadonlySet<string>): boolean {
	return d.vehicle_types.includes(v.vehicle_type) && !d.busy_ref && d.on_shift && !d.rest_day && !usedDriverIds.has(d.driver_id);
}

/** Her auto-pick (l3.js:2048): a driver who usually drives this vehicle first, then any who fits in the same cluster. */
export function pickDriverFor(v: TransportVehicleOption, drivers: readonly TransportDriverOption[], used: ReadonlySet<string>): TransportDriverOption | null {
	const fit = drivers.filter((d) => driverFits(d, v, used));
	return fit.find((d) => d.usual_vehicle_ids.includes(v.vehicle_id)) ?? fit.find((d) => d.in_dispatch_cluster) ?? null;
}
